import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  IconButton,
  Grid,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  Snackbar,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Collapse,
  Autocomplete,
  Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import BusinessIcon from '@mui/icons-material/Business';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import firebaseDataService from '../../../services/FirebaseDataService';
import { useAuth } from '../../../contexts/AuthContext';
import { ACCOUNTING_API, handleApiError } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import { useTranslation } from 'react-i18next';

// Örnek grup veri yapısı (düz yapı)
interface AccountGroup {
  id: string;
  name: string;
  parentId: string | null;
  level: number;
  createdAt: string;
  branchesId?: string | null;
}

interface Branch {
  id: string;
  name: string;
}

interface AccountGroupModalProps {
  open: boolean;
  onClose: () => void;
  branchId?: string;
  isManager?: boolean;
}

const AccountGroupModal: React.FC<AccountGroupModalProps> = ({ 
  open, 
  onClose, 
  branchId, 
  isManager = false 
}) => {
  const { t, i18n } = useTranslation('accounting');
  // ✅ Merkezi Cache Sistemi - AuthContext'ten al
  const { branches } = useAuth();
  
  const [groupName, setGroupName] = useState('');
  const [error, setError] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<AccountGroup | null>(null);
  const [groups, setGroups] = useState<AccountGroup[]>([]);
  const [filteredGroups, setFilteredGroups] = useState<AccountGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning'>('success');
  const [expandedGroups, setExpandedGroups] = useState<{ [key: string]: boolean }>({});
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);

  const localizeGroupName = (name: string) => {
    const normalized = name.trim().toLocaleLowerCase('tr-TR');
    const keyMap: Record<string, string> = {
      'bireysel cari': 'individualCurrent',
      'personel cari': 'personnelCurrent',
      'personal cari': 'personnelCurrent',
      'kurumsal cari': 'corporateCurrent',
      'genel cari grubu': 'generalCurrentGroup'
    };
    const key = keyMap[normalized];
    return key ? t(`current.groupNames.${key}`, { defaultValue: name }) : name;
  };
  
  // Şirket ID'sini, şubeleri ve grupları yükleme
  useEffect(() => {
    const loadCompanyAndData = async () => {
      try {
        setInitialLoading(true);
        
        // Kullanıcı oturum açmış mı kontrol et
        const uid = ownAuthSession.getCurrentUid();
        if (!uid) {
          setInitialLoading(false);
          return;
        }
        
        // Kullanıcının şirket ID'sini al (Cloud Functions)
        const userData = await firebaseDataService.getUserData(uid);
        
        if (!userData) {
          setInitialLoading(false);
          return;
        }
        
        const companyId = userData.companyId;
        setCompanyId(companyId || null);
        
        if (!companyId) {
          setInitialLoading(false);
          return;
        }
        
        // Cari grupları yükle
        loadAccountGroups(companyId);
        
      } catch (error) {
        console.error('Şirket veya veri yüklenirken hata:', error);
        setInitialLoading(false);
      }
    };
    
    if (open) {
      loadCompanyAndData();
    }
    
  // Cleanup
  return () => {
    // Cleanup artık gerekmiyor (Firebase listener yok)
  };
  }, [open, branchId]);

  // ✅ Şube müdürü için otomatik şube seçimi (branches değiştiğinde)
  useEffect(() => {
    if (isManager && branchId && branches.length > 0) {
      const managerBranch = branches.find(branch => branch.id === branchId);
      if (managerBranch) {
        setSelectedBranch(managerBranch);
        console.log(`Şube müdürü için otomatik şube seçildi: ${managerBranch.name} (${managerBranch.id})`);
      }
    }
  }, [branches, isManager, branchId]);
  
  // Cari gruplarını yükleme - Tamamen SQL'den
  const loadAccountGroups = async (companyId: string) => {
    try {
      console.log('SQL\'den cari gruplar yükleniyor...');
      
      const response = await accountingApi.get(ACCOUNTING_API.ACCOUNT_GROUP_BY_COMPANY);

      const result = response.data;
      console.log('SQL\'den gelen gruplar:', result);
      
      // API response formatı: {success: true, data: [...]}
      const sqlGroups = result.data || result;
      
      // SQL'den gelen verileri frontend formatına çevir
      const groupsList: AccountGroup[] = sqlGroups.map((data: any) => ({
        id: data.id.toString(), // SQL ID'yi string'e çevir
        name: data.name || '',
        parentId: data.parentId ? data.parentId.toString() : null, // Parent SQL ID
        level: data.level || 1,
        createdAt: data.createdAt || '',
        branchesId: data.branchId || null
      }));
      
      // Verileri seviyeye göre sıralayalım
      groupsList.sort((a, b) => {
        // Önce seviyeye göre sırala
        if (a.level !== b.level) {
          return a.level - b.level;
        }
        // Sonra isme göre sırala
        return a.name.localeCompare(b.name);
      });
      
      setGroups(groupsList);
      
      // Gruplara filtreleme uygula
      applyBranchFilter(groupsList, selectedBranch?.id);
      setInitialLoading(false);
      
    } catch (error) {
      console.error('SQL cari grupları yükleme hatası:', error);
      setError(handleApiError(error));
      setInitialLoading(false);
    }
  };
  
  // Şubeye göre grupları filtrele
  const applyBranchFilter = (allGroups: AccountGroup[], branchId: string | null | undefined) => {
    if (!branchId) {
      // Şube seçilmemişse veya managerBranchId yoksa tüm grupları göster
      setFilteredGroups(allGroups);
      return;
    }
    
    console.log(`Gruplar ${branchId} şube ID'sine göre filtreleniyor...`);
    
    // Şubeye ait grupları filtrele
    const filtered = allGroups.filter(group => {
      // Eğer parentId'si olan bir grup ise, üst grubun filtrelemesine bağlı
      if (group.parentId) {
        const parentGroup = allGroups.find(p => p.id === group.parentId);
        return parentGroup ? isBranchGroup(parentGroup, branchId) : false;
      }
      
      // Ana grup için doğrudan şube kontrolü yap
      return isBranchGroup(group, branchId);
    });
    
    console.log(`Filtreleme sonucu: ${filtered.length}/${allGroups.length} grup`);
    setFilteredGroups(filtered);
  };
  
  // Grubun verilen şubeye ait olup olmadığını kontrol et
  const isBranchGroup = (group: AccountGroup, branchId: string): boolean => {
    if (!group.branchesId) {
      // branchesId yoksa genel grup olabilir, şube müdürü yetkisine göre karar ver
      return !isManager; // Şube müdürü değilse göster, şube müdürü ise gizle
    }
    
    // branchesId obje formatında olabilir
    if (typeof group.branchesId === 'object' && group.branchesId !== null) {
      const branchKeys = Object.keys(group.branchesId);
      return branchKeys.includes(branchId);
    }
    
    // String formatında karşılaştırma
    return group.branchesId === branchId;
  };
  
  // Şube değiştiğinde grupları filtrele
  useEffect(() => {
    if (groups.length > 0) {
      applyBranchFilter(groups, selectedBranch?.id);
    }
  }, [selectedBranch, groups]);
  
  // Formun temizlenmesi
  const resetForm = () => {
    setGroupName('');
    setError('');
  };
  
  // Modal kapatılırken çalışacak handler
  const handleClose = () => {
    resetForm();
    setSelectedGroup(null);
    setInfoDialogOpen(false);
    onClose();
  };
  
  // Grup seçimi
  const handleSelectGroup = (group: AccountGroup) => {
    setSelectedGroup(group);
    
    // Eğer bir grup seçildiyse, onun şubesini otomatik olarak seç
    if (group) {
      if (group.branchesId) {
        // Seçilen grubun şubesi varsa o şubeyi bul ve seç
        const groupBranch = branches.find(b => b.id === group.branchesId);
        if (groupBranch) {
          setSelectedBranch(groupBranch);
        }
      } else {
        // Seçilen grubun şubesi yoksa (genel ise), seçimi temizle
        setSelectedBranch(null);
      }
    }
    
    resetForm();
  };
  
  // Grup genişletme/daraltma durumunu değiştir
  const toggleGroupExpand = (groupId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };
  
  // Bir grubun alt grupları var mı kontrol et
  const hasChildGroups = (groupId: string) => {
    return filteredGroups.some(group => group.parentId === groupId);
  };
  
  // Grup silme - SQL API
  const handleDeleteGroup = async (group: AccountGroup, event: React.MouseEvent) => {
    event.stopPropagation();
    
    if (!companyId) {
      setSnackbarMessage(t('current.modals.groupManagement.errors.companyNotFound'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    
    try {
      setLoading(true);
      
      // Alt grupları kontrol et
      const hasChildren = filteredGroups.some(g => g.parentId === group.id);
      
      if (hasChildren) {
        setSnackbarMessage(t('current.modals.groupManagement.errors.hasChildGroups'));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setLoading(false);
        return;
      }
      
      // SQL API'ye silme isteği gönder
      const numericId = parseInt(group.id, 10);
      
      // ✅ MULTI-TENANT: companyId ile güvenli silme
      await accountingApi.post(ACCOUNTING_API.ACCOUNT_GROUP_DELETE(numericId));

      // Başarı mesajı
      setSnackbarMessage(t('current.modals.groupManagement.messages.groupDeleted'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Eğer silinen grup seçili grupsa, seçimi temizle
      if (selectedGroup?.id === group.id) {
        setSelectedGroup(null);
      }
      
      // Grupları yeniden yükle
      await loadAccountGroups(companyId);
      
    } catch (error) {
      console.error('Grup silinirken hata:', error);
      setSnackbarMessage(handleApiError(error));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };
  
  // Yeni grup ekleme - SQL API
  const handleAddGroup = async () => {
    if (!groupName.trim()) {
      setError(t('current.modals.groupManagement.errors.groupNameRequired'));
      return;
    }
    
    if (!companyId) {
      setError(t('current.modals.groupManagement.errors.companyNotFound'));
      return;
    }
    
    let newGroupLevel = 1; // Default: ana grup (level 1)
    let parentSqlId = null;
    let newGroupBranchesId = null;
    
    // Eğer bir grup seçilmişse, onun alt grubu olarak ekle
    if (selectedGroup) {
      // Seçilen grup 3. seviyeyse, alt grup eklenemez
      if (selectedGroup.level >= 3) {
        setError(t('current.modals.groupManagement.errors.maxLevelReached'));
        return;
      }
      
      newGroupLevel = selectedGroup.level + 1;
      parentSqlId = parseInt(selectedGroup.id, 10); // Parent SQL ID
      // Üst grubun şube bilgisini al
      newGroupBranchesId = selectedGroup.branchesId;
    } else {
      // Ana grup ekleniyor, seçilen şubeyi kullan
      newGroupBranchesId = selectedBranch?.id || (isManager && branchId ? branchId : null);
    }
    
    try {
      setLoading(true);
      
      // SQL API için veri hazırla
      const groupPayload = {
        companyId: companyId,
        name: groupName.trim(),
        parentId: parentSqlId, // Parent SQL ID (integer)
        level: newGroupLevel,
        branchId: newGroupBranchesId,
        createdAt: new Date().toISOString(),
        createdBy: ownAuthSession.getCurrentUid() || null,
      };
      
      console.log('🔄 SQL API\'ye grup gönderiliyor:', groupPayload);
      
      // SQL API'ye POST isteği
      const response = await accountingApi.post(ACCOUNTING_API.ACCOUNT_GROUPS, groupPayload);

      const result = response.data;
      console.log('✅ Grup SQL\'e başarıyla kaydedildi:', result);
      
      // Başarı mesajı
      setSnackbarMessage(t('current.modals.groupManagement.messages.groupAdded'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Formu sıfırla
      resetForm();
      
      // Grupları yeniden yükle
      await loadAccountGroups(companyId);
      
    } catch (error) {
      console.error('❌ Grup eklenirken hata:', error);
      setError(error instanceof Error ? error.message : t('current.modals.groupManagement.errors.groupAddFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Ağaç yapısı için ana grupları bul (parentId'si null olanlar)
  const getRootGroups = () => {
    console.log('Ana gruplar bulunuyor, mevcut gruplar:', filteredGroups);
    const rootGroups = filteredGroups.filter(group => group.parentId === null);
    console.log('Bulunan ana gruplar:', rootGroups);
    return rootGroups;
  };
  
  // Bir grubun alt gruplarını bul
  const getChildGroups = (parentId: string) => {
    return filteredGroups.filter(group => group.parentId === parentId);
  };
  
  // Ana grupları ve genişletilmiş grupların alt gruplarını içeren düzleştirilmiş liste oluştur
  const getFilteredHierarchicalList = () => {
    const result: AccountGroup[] = [];
    
    // Ana grupları ekle
    const rootGroups = getRootGroups();
    rootGroups.forEach(rootGroup => {
      // Ana grubu ekle
      result.push(rootGroup);
      
      // Eğer grup genişletilmişse, alt grupları rekürsif olarak ekle
      if (expandedGroups[rootGroup.id]) {
        addVisibleChildGroups(rootGroup.id, result, new Set([rootGroup.id]));
      }
    });
    
    return result;
  };
  
  // Görünür alt grupları rekürsif olarak ekle
  const addVisibleChildGroups = (parentId: string, list: AccountGroup[], expandedParents: Set<string>) => {
    const childGroups = getChildGroups(parentId);
    
    // Alt grupları isme göre sırala
    childGroups.sort((a, b) => a.name.localeCompare(b.name));
    
    childGroups.forEach(childGroup => {
      // Alt grubu listeye ekle
      list.push(childGroup);
      
      // Eğer bu alt grup genişletilmişse, onun alt gruplarını da ekle
      if (expandedGroups[childGroup.id]) {
        // Döngüsel bağımlılıkları önlemek için kontrol
        if (!expandedParents.has(childGroup.id)) {
          const newExpandedParents = new Set(expandedParents);
          newExpandedParents.add(childGroup.id);
          addVisibleChildGroups(childGroup.id, list, newExpandedParents);
        }
      }
    });
  };

  // Şube adını getir
  const getBranchName = (branchId: string | null | undefined): string => {
    if (!branchId) return t('current.modals.groupManagement.general');
    
    const branch = branches.find(b => b.id === branchId);
    return branch ? branch.name : t('current.modals.groupManagement.unknownBranch');
  };
  
  // Mevcut seçimin durumuna göre başlık
  const getFormTitle = () => {
    if (!selectedGroup) {
      return t('current.modals.groupManagement.addRootGroup');
    }
    return t('current.modals.groupManagement.addSubGroupTo', { groupName: localizeGroupName(selectedGroup.name) });
  };
  
  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{t('current.modals.groupManagement.title')}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton edge="end" onClick={() => setInfoDialogOpen(true)} aria-label={t('current.menu.information')}>
                <InfoOutlinedIcon />
              </IconButton>
              <IconButton edge="end" onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          {initialLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
            {/* Form Alanı */}
            <Grid item xs={12} md={5}>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', height: '100%' }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {getFormTitle()}
                </Typography>
                
                {selectedGroup && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {selectedGroup.level < 3 
                      ? t('current.modals.groupManagement.subGroupAddingInfo', { groupName: localizeGroupName(selectedGroup.name) })
                      : t('current.modals.groupManagement.maxLevelInfo', { groupName: localizeGroupName(selectedGroup.name) })
                    }
                  </Alert>
                )}
                
                {/* Şube seçim alanı */}
                <Autocomplete
                  fullWidth
                  value={selectedBranch}
                  onChange={(event, newValue) => {
                    if (!(isManager && branchId) && !selectedGroup) {
                      setSelectedBranch(newValue);
                    }
                  }}
                  options={branches}
                  getOptionLabel={(option) => option.name}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <BusinessIcon fontSize="small" sx={{ mr: 1, color: 'primary.main', fontSize: '0.875rem' }} />
                        <Typography variant="body2">{option.name}</Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        selectedGroup 
                          ? t('current.modals.groupManagement.branchForSelectedGroup', { branchName: selectedGroup.branchesId ? getBranchName(selectedGroup.branchesId) : t('current.modals.groupManagement.general') })
                          : (isManager && branchId ? t('current.modals.groupManagement.branchManagerLabel') : t('current.modals.groupManagement.branchFilterLabel'))
                      }
                      size="small"
                      placeholder={selectedGroup ? t('current.modals.groupManagement.branchPlaceholderSelected') : t('current.modals.groupManagement.branchPlaceholder')}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <BusinessIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.2rem' }} />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                        readOnly: isManager && branchId ? true : false
                      }}
                      disabled={!!((isManager && branchId) || selectedGroup !== null)}
                      sx={{ mb: 2 }}
                    />
                  )}
                  disableClearable={!!((isManager && branchId) || selectedGroup !== null)}
                  disabled={!!((isManager && branchId) || selectedGroup !== null)}
                />
                
                {/* Şube seçiliyse veya üst grup seçiliyse bilgi mesajı göster */}
                {(selectedBranch || selectedGroup) && (
                  <Alert 
                    severity="info" 
                    variant="outlined" 
                    sx={{ mt: 1, mb: 2, fontSize: '0.75rem', py: 0.5 }}
                    icon={<BusinessIcon sx={{ fontSize: '1rem' }} />}
                  >
                    <Box>
                      {selectedGroup ? (
                        <>
                          <Typography variant="caption" display="block" fontWeight="bold">
                            Alt grup, üst grubun şubesi ile ilişkilendirilecek
                          </Typography>
                          <Typography variant="caption" display="block">
                            Şube: {selectedGroup.branchesId ? getBranchName(selectedGroup.branchesId) : 'Genel'}
                          </Typography>
                        </>
                      ) : selectedBranch ? (
                        <>
                          <Typography variant="caption" display="block" fontWeight="bold">
                            "{selectedBranch.name}" şubesi seçildi
                            {isManager && branchId ? ` ${t('current.modals.groupManagement.branchManagerSuffix')}` : ""}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Gruplar bu şubeyle ilişkilendirilecek
                          </Typography>
                        </>
                      ) : null}
                    </Box>
                  </Alert>
                )}
                
                <TextField
                  autoFocus
                  margin="dense"
                  id="groupName"
                      label={t('current.modals.groupManagement.groupName')}
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={groupName}
                  onChange={(e) => {
                    setGroupName(e.target.value);
                    setError('');
                  }}
                  error={!!error}
                  helperText={error}
                  sx={{ mb: 2 }}
                  disabled={selectedGroup?.level === 3 || loading}
                />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  {selectedGroup && (
                    <Button 
                      variant="outlined" 
                      onClick={() => setSelectedGroup(null)}
                      disabled={loading}
                    >
                      {t('current.modals.groupManagement.clearSelection')}
                    </Button>
                  )}
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleAddGroup}
                    disabled={!groupName.trim() || selectedGroup?.level === 3 || loading}
                    fullWidth={!selectedGroup}
                    sx={{ ml: selectedGroup ? 2 : 0 }}
                    startIcon={loading ? <CircularProgress size={24} /> : null}
                  >
                    {loading 
                      ? t('current.modals.groupManagement.saving')
                      : (selectedGroup ? t('current.modals.groupManagement.addSubGroup') : t('current.modals.groupManagement.addGroup'))
                    }
                  </Button>
                </Box>
              </Paper>
            </Grid>
            
            {/* Ağaç Görünüm Alanı */}
            <Grid item xs={12} md={7}>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', height: '100%' }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {t('current.modals.groupManagement.existingGroups')}
                </Typography>
                
                <Divider sx={{ my: 2 }} />
                
                {filteredGroups.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {groups.length === 0 ? 
                      t('current.modals.groupManagement.noGroups') : 
                      t('current.modals.groupManagement.noGroupsForBranch')
                    }
                  </Typography>
                ) : (
                  <Box sx={{ overflowX: 'auto' }}>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('current.modals.groupManagement.totalGroups', { count: filteredGroups.length })}
                      </Typography>
                    </Box>
                    <Table size="small" sx={{ minWidth: 400 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('current.modals.groupManagement.groupName')}</TableCell>
                          <TableCell>{t('current.table.columns.branch')}</TableCell>
                          <TableCell>{t('current.modals.groupManagement.createdAt')}</TableCell>
                          <TableCell width="120px" align="right">{t('current.table.columns.actions')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {getFilteredHierarchicalList().map((group) => (
                          <TableRow 
                            key={group.id} 
                            hover
                            sx={{ 
                              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                              ...(selectedGroup?.id === group.id ? 
                                { backgroundColor: 'rgba(25, 118, 210, 0.12)' } : {}),
                              cursor: 'pointer'
                            }}
                            onClick={() => handleSelectGroup(group)}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {/* Seviyeye göre girinti ekle */}
                                {Array(group.level - 1).fill(0).map((_, index) => (
                                  <Box key={index} sx={{ width: 20 }} />
                                ))}
                                
                                {/* Genişletme/daraltma ikonu */}
                                {hasChildGroups(group.id) ? (
                                  <IconButton
                                    size="small"
                                    onClick={(e) => toggleGroupExpand(group.id, e)}
                                    sx={{ p: 0.5, mr: 1 }}
                                  >
                                    {expandedGroups[group.id] ? 
                                      <KeyboardArrowDownIcon fontSize="small" /> : 
                                      <KeyboardArrowRightIcon fontSize="small" />
                                    }
                                  </IconButton>
                                ) : (
                                  <Box sx={{ width: 28, height: 28, mr: 1 }} /> // Boşluk bırak
                                )}
                                
                                <Typography variant="body2" sx={{ 
                                  fontWeight: selectedGroup?.id === group.id ? 'bold' : 'normal' 
                                }}>
                                  {localizeGroupName(group.name)}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {getBranchName(group.branchesId)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {new Date(group.createdAt).toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'tr-TR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                {group.level < 3 && (
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSelectGroup(group);
                                    }}
                                    sx={{ color: 'primary.main' }}
                                    disabled={loading}
                                    title={t('current.modals.groupManagement.addSubGroup')}
                                  >
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                )}
                                {/* Silme butonu - "Genel Cari Grubu" ise devre dışı bırak */}
                                <Tooltip title={group.name === "Genel Cari Grubu" ? t('current.modals.groupManagement.generalGroupDeleteBlocked') : ""}>
                                  <span> {/* Tooltip'in disabled butonda çalışması için span */} 
                                    <IconButton
                                      size="small"
                                      onClick={(e) => handleDeleteGroup(group, e)}
                                      sx={{ color: 'error.main' }}
                                      disabled={loading || group.name === "Genel Cari Grubu"} // Genel Cari Grubu ise butonu devre dışı bırak
                                      title={t('current.modals.groupManagement.delete')}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose} color="inherit" disabled={loading}>{t('current.actions.close')}</Button>
        </DialogActions>

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
        >
          <Alert 
            onClose={() => setSnackbarOpen(false)} 
            severity={snackbarSeverity} 
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Dialog>

      <Dialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('current.modals.groupManagement.infoDialog.title', { defaultValue: 'Cari Grup Yönetimi Bilgilendirme' })}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('current.modals.groupManagement.infoDialog.description', {
              defaultValue:
                'Cari grup yönetimi, cari hesapları düzenli bir sınıflama yapısıyla takip etmenizi sağlar. Doğru grup yapısı, raporlama ve operasyon süreçlerini hızlandırır.'
            })}
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            {t('current.modals.groupManagement.infoDialog.structureTitle', { defaultValue: 'Grup Yapısı Nasıl Çalışır?' })}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {t('current.modals.groupManagement.infoDialog.structureItem1', {
              defaultValue: '• Sistem ana grup ve alt grup mantığında çalışır; alt gruplar üst grubun altında listelenir.'
            })}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t('current.modals.groupManagement.infoDialog.structureItem2', {
              defaultValue: '• En fazla 3 seviye grup desteklenir. Bu sınır, yapının karmaşıklaşmasını önlemek için uygulanır.'
            })}
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            {t('current.modals.groupManagement.infoDialog.branchTitle', { defaultValue: 'Şube ve Yetki İlişkisi' })}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t('current.modals.groupManagement.infoDialog.branchText', {
              defaultValue:
                'Ana grup oluştururken seçilen şube bilgisi, o grubun kapsamını belirler. Alt grup eklenirken şube bilgisi üst gruptan miras alınır; böylece hiyerarşi tutarlı kalır.'
            })}
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            {t('current.modals.groupManagement.infoDialog.usageTitle', { defaultValue: 'Ne Zaman Hangi Grubu Açmalıyım?' })}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {t('current.modals.groupManagement.infoDialog.usageItem1', {
              defaultValue: '• Ana grupları, raporda ayrı takip etmek istediğiniz büyük iş kategorileri için açın.'
            })}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t('current.modals.groupManagement.infoDialog.usageItem2', {
              defaultValue: '• Alt grupları, aynı ana kategori içindeki farklı müşteri/iş modeli kırılımlarını ayırmak için kullanın.'
            })}
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            {t('current.modals.groupManagement.infoDialog.deleteTitle', { defaultValue: 'Silme ve Güvenlik Kuralları' })}
          </Typography>
          <Typography variant="body2">
            {t('current.modals.groupManagement.infoDialog.deleteText', {
              defaultValue:
                'Bir grubun altında alt grup varsa doğrudan silinemez. Ayrıca sistem için zorunlu olan temel grup kayıtları silmeye kapalı olabilir; bu durum veri bütünlüğünü korumak içindir.'
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialogOpen(false)}>
            {t('current.actions.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AccountGroupModal; 